<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { ArrowRight, Check, Download, EyeOff, Flame, Gauge, ShieldCheck, Trophy } from "lucide-vue-next";
import { RouterLink } from "vue-router";
import { API_BASE, BRAND_LOGO_URL, MAC_APP_DOWNLOAD_URL } from "@/shared/config/constants";
import { compact } from "@/shared/utils/format";

const leaderboardRows = ref([]);
const leaderboardState = ref("loading");
const routeMetaNodes = [];
let previousTitle = document.title;

const proofRows = computed(() => leaderboardRows.value.slice(0, 5));
const communityTotal = computed(() => leaderboardRows.value.reduce((total, row) => total + (row.totalTokens || 0), 0));
const communityTotalText = computed(() => {
  if (leaderboardState.value === "loading") return "读取中";
  if (leaderboardState.value === "error") return "—";
  return compact.format(communityTotal.value);
});

const valueCards = [
  {
    index: "01",
    icon: Flame,
    title: "让行动被看见",
    body: "Agent 时代，真正重要的不是说了什么，而是每天调用了多少能力、完成了多少工作。Token 是一张朴素的行动凭证。",
  },
  {
    index: "02",
    icon: Trophy,
    title: "和熟人一起向前",
    body: "排行榜里是一起学习、一起做项目的熊友。把个人用量放进社区坐标，持续实践就有了真实反馈。",
  },
  {
    index: "03",
    icon: ShieldCheck,
    title: "只同步必要数据",
    body: "Mac App 只上传每日汇总。对话、代码、终端输出、项目路径和本机目录结构都不会离开你的电脑。",
  },
];

const steps = [
  {
    index: "01",
    title: "下载 Mac App",
    body: "把小能熊 Token Economics 放进菜单栏。本地读取 Codex 与 Claude Code 的每日汇总。",
  },
  {
    index: "02",
    title: "用微信完成配对",
    body: "浏览器中确认一次设备授权，上传凭证只保存在这台 Mac 的 Keychain。",
  },
  {
    index: "03",
    title: "照常使用 Agent",
    body: "历史数据自动补齐，此后按刷新节奏同步今天与昨天。打开排行榜，就能看到进度。",
  },
];

const uploadFields = ["日期与 Agent 类型", "输入 / 输出 Token", "缓存 Token", "模型列表", "CodexBar 本地成本估算"];
const privateFields = ["对话与 Prompt", "代码与 Tool Output", "cwd 与项目路径", "Thread / Session 明细", "原始 JSONL 文件"];

const faqs = [
  {
    question: "这是一个计费工具吗？",
    answer: "不是。页面里的成本是 CodexBar 在本机生成的估算值，不是 OpenAI 或 Anthropic 的官方账单。Token Economics 关注的是社区里的 Agent 实践强度。",
  },
  {
    question: "目前支持哪些 Agent？",
    answer: "当前排行榜支持 Codex 与 Claude Code。两者都由 Mac App 在本地读取每日汇总，再同步到社区榜单。",
  },
  {
    question: "需要每天手动上传吗？",
    answer: "不需要。完成一次设备配对后，App 会自动同步；没有对应日期数据时会跳过，不会编造用量。",
  },
  {
    question: "为什么是每天 1 亿 Token？",
    answer: "这是小能熊社区用来推动行动的挑战目标。它不是效率结论，而是一面刻意练习的计分板：持续、可见、能复盘。",
  },
];

function addRouteMeta(property, content) {
  const node = document.createElement("meta");
  node.setAttribute(property.startsWith("og:") ? "property" : "name", property);
  node.setAttribute("content", content);
  node.dataset.aboutV2Meta = "true";
  document.head.append(node);
  routeMetaNodes.push(node);
}

async function loadLeaderboardProof() {
  try {
    const response = await fetch(`${API_BASE}/public/leaderboard/total?provider=all&limit=100`, {
      credentials: "include",
      headers: { accept: "application/json" },
    });
    if (!response.ok) throw new Error(`leaderboard ${response.status}`);
    const data = await response.json();
    leaderboardRows.value = Array.isArray(data.items) ? data.items : [];
    leaderboardState.value = "ready";
  } catch {
    leaderboardRows.value = [];
    leaderboardState.value = "error";
  }
}

onMounted(() => {
  previousTitle = document.title;
  document.title = "Token Economics · 把 Agent 用进工作里";
  addRouteMeta("description", "一次安装，自动汇总 Codex 与 Claude Code 的每日 Token 用量。和小能熊社区一起，把 Agent 实践变成看得见的进度。");
  addRouteMeta("og:title", "Token Economics · 每天 1 亿 Token");
  addRouteMeta("og:description", "自动统计、社区排行、只同步每日汇总。把 Agent 用进工作里。");
  addRouteMeta("og:image", "https://ai.candobear.com/tokenomics/aboutv2-og.png");
  addRouteMeta("twitter:card", "summary_large_image");
  addRouteMeta("twitter:image", "https://ai.candobear.com/tokenomics/aboutv2-og.png");
  loadLeaderboardProof();
});

onBeforeUnmount(() => {
  document.title = previousTitle;
  routeMetaNodes.splice(0).forEach((node) => node.remove());
});
</script>

<template>
  <main class="v2-page">
    <header class="v2-topbar">
      <RouterLink class="v2-brand" :to="{ name: 'about-v2' }" aria-label="Token Economics V2 首页">
        <img :src="BRAND_LOGO_URL" alt="" />
        <span>token economics</span>
      </RouterLink>
      <nav class="v2-nav" aria-label="页面导航">
        <a href="#why">为什么</a>
        <a href="#how">如何开始</a>
        <a href="#privacy">隐私</a>
        <RouterLink class="v2-nav-board" :to="{ name: 'leaderboard' }">打开排行榜</RouterLink>
      </nav>
    </header>

    <section class="v2-hero">
      <div class="v2-hero-copy">
        <div class="v2-kicker"><span></span> 小能熊 · Agent 实践计分板</div>
        <h1>
          <span>每天 1 亿 Token，</span>
          <strong>把 Agent 用进工作里。</strong>
        </h1>
        <p>
          一次安装，自动汇总 Codex 与 Claude Code 的每日用量。<br />
          和小能熊社区一起，把“拥抱 AI”变成看得见的行动。
        </p>
        <div class="v2-hero-actions">
          <a class="v2-button v2-button-primary" :href="MAC_APP_DOWNLOAD_URL">
            <Download :size="18" :stroke-width="2.4" />下载 Mac App
          </a>
          <RouterLink class="v2-button v2-button-secondary" :to="{ name: 'leaderboard' }">
            看实时排行榜 <ArrowRight :size="18" :stroke-width="2.4" />
          </RouterLink>
        </div>
        <div class="v2-hero-note">
          <ShieldCheck :size="17" :stroke-width="2.3" />
          只同步每日汇总，不上传对话、代码或项目路径
        </div>
      </div>

      <aside class="v2-proof-card" aria-label="实时社区总量榜预览">
        <div class="v2-proof-topline">
          <span class="v2-live-dot"></span><span>community proof</span><span>live</span>
        </div>
        <div class="v2-proof-total">
          <span>社区已上传总量</span>
          <strong>{{ communityTotalText }}</strong>
          <small>tokens</small>
        </div>
        <div class="v2-proof-rule"></div>
        <div class="v2-proof-head"><span>rank / member</span><span>total</span></div>
        <div v-if="leaderboardState === 'loading'" class="v2-proof-status" aria-live="polite">正在读取实时榜单…</div>
        <div v-else-if="leaderboardState === 'error'" class="v2-proof-status" aria-live="polite">榜单暂时不可用，稍后再来看看。</div>
        <div v-else-if="proofRows.length === 0" class="v2-proof-status" aria-live="polite">等待第一位成员上传数据。</div>
        <ol v-else class="v2-proof-list">
          <li v-for="row in proofRows" :key="row.user?.id || row.rank">
            <span class="v2-proof-rank">{{ String(row.rank).padStart(2, "0") }}</span>
            <img v-if="row.user?.avatarUrl" :src="row.user.avatarUrl" alt="" />
            <span v-else class="v2-proof-avatar">{{ (row.user?.displayName || "熊").slice(0, 1) }}</span>
            <strong>{{ row.user?.displayName || `成员 ${row.rank}` }}</strong>
            <em>{{ compact.format(row.totalTokens) }}</em>
          </li>
        </ol>
        <div class="v2-proof-target">
          <div><span>daily target</span><strong>100,000,000</strong></div>
          <Gauge :size="30" :stroke-width="1.8" />
        </div>
      </aside>
    </section>

    <section class="v2-ticker" aria-label="产品特点">
      <span>Codex</span><i></i><span>Claude Code</span><i></i><span>Daily Summary</span><i></i><span>Community Ranking</span>
    </section>

    <section id="why" class="v2-section v2-values">
      <div class="v2-section-heading">
        <span>01 / WHY</span>
        <h2>把 Agent 使用量，变成一场长期游戏。</h2>
        <p>计量不是终点。看见行动、获得反馈、持续实践，才是 Token Economics 想帮助你完成的事。</p>
      </div>
      <div class="v2-value-grid">
        <article v-for="card in valueCards" :key="card.index" class="v2-value-card">
          <div class="v2-card-meta">
            <span>{{ card.index }}</span>
            <component :is="card.icon" :size="23" :stroke-width="2" />
          </div>
          <h3>{{ card.title }}</h3>
          <p>{{ card.body }}</p>
        </article>
      </div>
    </section>

    <section class="v2-statement">
      <p>“别只谈论 Agent 革命。</p>
      <strong>把 Token 烧出来。”</strong>
      <span>— 小能熊 · 做中学</span>
    </section>

    <section id="how" class="v2-section v2-how">
      <div class="v2-section-heading v2-section-heading-light">
        <span>02 / HOW</span><h2>三步开始，之后保持自动。</h2>
      </div>
      <div class="v2-step-list">
        <article v-for="step in steps" :key="step.index" class="v2-step">
          <span>{{ step.index }}</span>
          <div><h3>{{ step.title }}</h3><p>{{ step.body }}</p></div>
          <ArrowRight :size="24" :stroke-width="1.8" />
        </article>
      </div>
      <a class="v2-button v2-button-amber" :href="MAC_APP_DOWNLOAD_URL">
        下载并开始 <ArrowRight :size="18" :stroke-width="2.4" />
      </a>
    </section>

    <section id="privacy" class="v2-section v2-privacy">
      <div class="v2-privacy-intro">
        <div class="v2-privacy-icon"><EyeOff :size="34" :stroke-width="1.8" /></div>
        <span>03 / PRIVACY</span>
        <h2>你的工作内容，不是排行榜的数据。</h2>
        <p>Token Economics 坚守 summary-only 边界。排行榜只需要知道每天用了多少，不需要知道你具体做了什么。</p>
      </div>
      <div class="v2-privacy-grid">
        <article class="v2-data-card v2-data-card-upload">
          <span>会同步</span><h3>每日汇总</h3>
          <ul><li v-for="field in uploadFields" :key="field"><Check :size="17" :stroke-width="2.5" />{{ field }}</li></ul>
        </article>
        <article class="v2-data-card v2-data-card-private">
          <span>永不上传</span><h3>你的内容</h3>
          <ul><li v-for="field in privateFields" :key="field"><span>×</span>{{ field }}</li></ul>
        </article>
      </div>
    </section>

    <section class="v2-section v2-faq">
      <div class="v2-section-heading"><span>04 / FAQ</span><h2>开始前，可能还想知道。</h2></div>
      <div class="v2-faq-list">
        <details v-for="faq in faqs" :key="faq.question">
          <summary>{{ faq.question }}<span>+</span></summary>
          <p>{{ faq.answer }}</p>
        </details>
      </div>
    </section>

    <section class="v2-final">
      <img :src="BRAND_LOGO_URL" alt="做中学熊" />
      <span>READY TO BURN?</span>
      <h2>今天，就把 Agent 用起来。</h2>
      <p>每天 1 亿 Token。和一群认真做事的人，一起进入 Agent 新时代。</p>
      <div class="v2-final-actions">
        <a class="v2-button v2-button-primary" :href="MAC_APP_DOWNLOAD_URL"><Download :size="18" />下载 Mac App</a>
        <RouterLink class="v2-final-link" :to="{ name: 'leaderboard' }">打开排行榜 <ArrowRight :size="17" /></RouterLink>
      </div>
    </section>

    <footer class="v2-footer">
      <div class="v2-brand v2-brand-footer"><img :src="BRAND_LOGO_URL" alt="" /><span>token economics</span></div>
      <p>学 AI，做中学 · 小能熊 AI 社群</p>
      <RouterLink :to="{ name: 'about' }">查看 About V1</RouterLink>
    </footer>
  </main>
</template>

<style scoped>
.v2-page {
  --v2-amber: #ffc400;
  --v2-ink: #0a0a0a;
  --v2-paper: #f7f3e8;
  --v2-white: #fffdf7;
  min-height: 100vh;
  overflow: hidden;
  background: var(--v2-paper);
  color: var(--v2-ink);
  font-family: var(--font-sans);
}

.v2-topbar {
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  min-height: 72px;
  padding: 0 clamp(20px, 4vw, 64px);
  border-bottom: 2px solid var(--v2-ink);
  background: rgb(247 243 232 / 94%);
  backdrop-filter: blur(14px);
}

.v2-brand,
.v2-nav,
.v2-hero-actions,
.v2-final-actions {
  display: flex;
  align-items: center;
}

.v2-brand {
  gap: 11px;
  color: inherit;
  text-decoration: none;
}

.v2-brand img {
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.v2-brand span,
.v2-kicker,
.v2-proof-card,
.v2-ticker,
.v2-section-heading > span,
.v2-privacy-intro > span {
  font-family: var(--font-mono);
}

.v2-brand span {
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.03em;
  text-transform: uppercase;
}

.v2-nav {
  gap: 28px;
}

.v2-nav a {
  color: inherit;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
}

.v2-nav a:not(.v2-nav-board):hover {
  text-decoration: underline;
  text-underline-offset: 5px;
}

.v2-nav .v2-nav-board {
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  padding: 0 20px;
  border-radius: 999px;
  background: var(--v2-ink);
  color: var(--v2-amber);
  font-weight: 900;
}

.v2-hero {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(420px, 0.92fr);
  gap: clamp(48px, 7vw, 112px);
  align-items: center;
  width: min(100% - 48px, 1360px);
  min-height: calc(100vh - 72px);
  margin: 0 auto;
  padding: 88px 0 96px;
}

.v2-hero::before {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 55%;
  width: 1px;
  background: rgb(10 10 10 / 10%);
  content: "";
}

.v2-hero-copy,
.v2-proof-card {
  position: relative;
  z-index: 1;
}

.v2-kicker,
.v2-hero-note {
  display: flex;
  align-items: center;
  gap: 10px;
}

.v2-kicker {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.v2-kicker span,
.v2-live-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--v2-amber);
  box-shadow: 0 0 0 3px var(--v2-ink);
}

.v2-hero h1 {
  max-width: 760px;
  margin-top: 28px;
  font-size: clamp(60px, 6.4vw, 104px);
  font-weight: 900;
  letter-spacing: -0.075em;
  line-height: 0.94;
  text-wrap: balance;
}

.v2-hero h1 span,
.v2-hero h1 strong {
  display: block;
}

.v2-hero h1 span {
  color: transparent;
  -webkit-text-stroke: 2px var(--v2-ink);
}

.v2-hero h1 strong {
  position: relative;
  z-index: 0;
  margin-top: 10px;
  font-weight: inherit;
}

.v2-hero h1 strong::after {
  position: absolute;
  right: -10px;
  bottom: 4px;
  left: -8px;
  z-index: -1;
  height: 26%;
  background: var(--v2-amber);
  content: "";
  transform: rotate(-1.2deg);
}

.v2-hero-copy > p {
  margin-top: 30px;
  color: #3d3b35;
  font-size: clamp(17px, 1.5vw, 21px);
  line-height: 1.75;
}

.v2-hero-actions,
.v2-final-actions {
  flex-wrap: wrap;
  gap: 14px;
}

.v2-hero-actions {
  margin-top: 36px;
}

.v2-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 54px;
  padding: 0 28px;
  border: 2px solid var(--v2-ink);
  border-radius: 999px;
  color: var(--v2-ink);
  font-size: 15px;
  font-weight: 900;
  text-decoration: none;
  transition: transform 140ms ease, box-shadow 140ms ease, background 140ms ease;
}

.v2-button:hover {
  transform: translateY(-2px);
}

.v2-button-primary {
  background: var(--v2-ink);
  color: var(--v2-amber);
  box-shadow: 5px 5px 0 var(--v2-amber);
}

.v2-button-primary:hover {
  box-shadow: 7px 7px 0 var(--v2-amber);
}

.v2-button-secondary {
  background: transparent;
}

.v2-button-secondary:hover {
  background: var(--v2-white);
}

.v2-hero-note {
  margin-top: 24px;
  color: #5b584f;
  font-size: 13px;
  font-weight: 700;
}

.v2-proof-card {
  width: 100%;
  border: 2px solid var(--v2-ink);
  background: var(--v2-ink);
  color: var(--v2-white);
  box-shadow: 12px 12px 0 var(--v2-amber);
  transform: rotate(1.2deg);
}

.v2-proof-topline,
.v2-proof-head,
.v2-proof-target {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.v2-proof-topline {
  justify-content: flex-start;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid #3b3b3b;
  color: #a8a8a8;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.v2-proof-topline span:last-child {
  margin-left: auto;
  color: var(--v2-amber);
}

.v2-live-dot {
  width: 6px;
  height: 6px;
  box-shadow: 0 0 0 3px rgb(255 196 0 / 18%);
}

.v2-proof-total {
  padding: 26px 24px 22px;
}

.v2-proof-total span,
.v2-proof-total small,
.v2-proof-head {
  color: #939393;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.v2-proof-total strong {
  display: block;
  margin-top: 9px;
  color: var(--v2-amber);
  font-size: clamp(42px, 5vw, 72px);
  font-weight: 900;
  letter-spacing: -0.07em;
  line-height: 1;
}

.v2-proof-total small {
  display: block;
  margin-top: 5px;
}

.v2-proof-rule {
  height: 8px;
  margin: 0 24px;
  background: linear-gradient(90deg, var(--v2-amber) 0 68%, #2d2d2d 68% 100%);
}

.v2-proof-head {
  padding: 20px 24px 10px;
}

.v2-proof-status {
  min-height: 164px;
  padding: 42px 24px;
  color: #a8a8a8;
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 700;
}

.v2-proof-list {
  margin: 0;
  padding: 0 24px 16px;
  list-style: none;
}

.v2-proof-list li {
  display: grid;
  grid-template-columns: 26px 32px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  border-top: 1px solid #2f2f2f;
}

.v2-proof-list img,
.v2-proof-avatar {
  width: 30px;
  height: 30px;
  border: 1px solid var(--v2-amber);
  border-radius: 50%;
  object-fit: cover;
}

.v2-proof-avatar {
  display: grid;
  place-items: center;
  background: #252525;
  color: var(--v2-amber);
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 900;
}

.v2-proof-rank {
  color: #777;
  font-size: 11px;
}

.v2-proof-list strong {
  min-width: 0;
  overflow: hidden;
  font-family: var(--font-sans);
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v2-proof-list em {
  color: var(--v2-amber);
  font-size: 12px;
  font-style: normal;
  font-weight: 800;
}

.v2-proof-target {
  padding: 20px 24px;
  background: var(--v2-amber);
  color: var(--v2-ink);
}

.v2-proof-target span {
  display: block;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.v2-proof-target strong {
  display: block;
  margin-top: 3px;
  font-size: 19px;
  font-weight: 900;
}

.v2-ticker {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(18px, 3vw, 48px);
  min-height: 74px;
  padding: 14px 24px;
  border-block: 2px solid var(--v2-ink);
  background: var(--v2-amber);
  font-size: clamp(12px, 1.2vw, 15px);
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.v2-ticker i {
  width: 8px;
  height: 8px;
  flex: 0 0 auto;
  background: var(--v2-ink);
  transform: rotate(45deg);
}

.v2-section {
  padding: 112px clamp(24px, 5vw, 72px);
}

.v2-values,
.v2-faq {
  width: min(100%, 1320px);
  margin: 0 auto;
}

.v2-section-heading {
  display: grid;
  grid-template-columns: 130px minmax(0, 1.15fr) minmax(260px, 0.65fr);
  gap: 32px;
  align-items: start;
}

.v2-section-heading > span,
.v2-privacy-intro > span {
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.12em;
}

.v2-section-heading h2,
.v2-privacy-intro h2 {
  font-size: clamp(40px, 5vw, 72px);
  font-weight: 900;
  letter-spacing: -0.055em;
  line-height: 1.02;
  text-wrap: balance;
}

.v2-section-heading p,
.v2-privacy-intro p {
  color: #545149;
  font-size: 16px;
  line-height: 1.75;
}

.v2-value-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  margin-top: 72px;
  border: 2px solid var(--v2-ink);
  background: var(--v2-ink);
}

.v2-value-card {
  min-height: 360px;
  padding: 30px;
  background: var(--v2-white);
}

.v2-value-card + .v2-value-card {
  border-left: 2px solid var(--v2-ink);
}

.v2-card-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #77736b;
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 800;
}

.v2-value-card h3 {
  max-width: 280px;
  margin-top: 92px;
  font-size: 30px;
  font-weight: 900;
  letter-spacing: -0.045em;
}

.v2-value-card p {
  margin-top: 18px;
  color: #56524a;
  font-size: 15px;
  line-height: 1.72;
}

.v2-statement {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 120px 24px;
  border-block: 2px solid var(--v2-ink);
  background: var(--v2-amber);
  text-align: center;
}

.v2-statement p,
.v2-statement strong {
  font-size: clamp(50px, 7vw, 112px);
  font-weight: 900;
  letter-spacing: -0.075em;
  line-height: 0.96;
}

.v2-statement strong {
  color: var(--v2-paper);
  -webkit-text-stroke: 2px var(--v2-ink);
  text-shadow: 5px 5px 0 var(--v2-ink);
}

.v2-statement span {
  margin-top: 34px;
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.v2-how {
  background: var(--v2-ink);
  color: var(--v2-white);
}

.v2-how > * {
  width: min(100%, 1180px);
  margin-inline: auto;
}

.v2-section-heading-light {
  grid-template-columns: 130px minmax(0, 1fr);
}

.v2-step-list {
  margin-top: 64px;
  border-top: 1px solid #3a3a3a;
}

.v2-step {
  display: grid;
  grid-template-columns: 90px minmax(0, 1fr) auto;
  gap: 30px;
  align-items: center;
  padding: 34px 0;
  border-bottom: 1px solid #3a3a3a;
}

.v2-step > span {
  color: var(--v2-amber);
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 800;
}

.v2-step h3 {
  font-size: 27px;
  font-weight: 900;
  letter-spacing: -0.04em;
}

.v2-step p {
  max-width: 650px;
  margin-top: 7px;
  color: #a8a8a8;
  font-size: 15px;
  line-height: 1.65;
}

.v2-step > svg {
  color: var(--v2-amber);
}

.v2-how .v2-button-amber {
  width: max-content;
  margin-top: 48px;
  border-color: var(--v2-amber);
  background: var(--v2-amber);
}

.v2-privacy {
  display: grid;
  grid-template-columns: minmax(300px, 0.8fr) minmax(520px, 1.2fr);
  gap: clamp(60px, 8vw, 130px);
  align-items: center;
  width: min(100%, 1320px);
  margin: 0 auto;
}

.v2-privacy-icon {
  display: grid;
  width: 74px;
  height: 74px;
  margin-bottom: 38px;
  place-items: center;
  border: 2px solid var(--v2-ink);
  border-radius: 50%;
  background: var(--v2-amber);
  box-shadow: 5px 5px 0 var(--v2-ink);
}

.v2-privacy-intro h2 {
  margin-top: 22px;
}

.v2-privacy-intro p {
  margin-top: 24px;
}

.v2-privacy-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  border: 2px solid var(--v2-ink);
  box-shadow: 10px 10px 0 var(--v2-amber);
}

.v2-data-card {
  min-height: 420px;
  padding: 30px 26px;
}

.v2-data-card + .v2-data-card {
  border-left: 2px solid var(--v2-ink);
}

.v2-data-card > span {
  display: inline-flex;
  min-height: 26px;
  align-items: center;
  padding: 0 10px;
  border-radius: 999px;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.v2-data-card-upload {
  background: var(--v2-white);
}

.v2-data-card-upload > span {
  background: var(--v2-amber);
}

.v2-data-card-private {
  background: var(--v2-ink);
  color: var(--v2-white);
}

.v2-data-card-private > span {
  background: #2b2b2b;
  color: var(--v2-amber);
}

.v2-data-card h3 {
  margin-top: 24px;
  font-size: 30px;
  font-weight: 900;
  letter-spacing: -0.045em;
}

.v2-data-card ul {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin: 34px 0 0;
  padding: 0;
  list-style: none;
}

.v2-data-card li {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 14px;
  font-weight: 700;
}

.v2-data-card-private li {
  color: #b5b5b5;
}

.v2-data-card-private li > span {
  display: grid;
  width: 17px;
  height: 17px;
  place-items: center;
  border: 1px solid #666;
  border-radius: 50%;
  color: var(--v2-amber);
  font-family: var(--font-mono);
  font-size: 12px;
}

.v2-faq {
  border-top: 2px solid var(--v2-ink);
}

.v2-faq .v2-section-heading {
  grid-template-columns: 130px minmax(0, 1fr);
}

.v2-faq-list {
  max-width: 900px;
  margin: 64px 0 0 auto;
  border-top: 2px solid var(--v2-ink);
}

.v2-faq details {
  border-bottom: 2px solid var(--v2-ink);
}

.v2-faq summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 24px 4px;
  cursor: pointer;
  list-style: none;
  font-size: 19px;
  font-weight: 900;
}

.v2-faq summary::-webkit-details-marker {
  display: none;
}

.v2-faq summary span {
  font-family: var(--font-mono);
  font-size: 26px;
  font-weight: 400;
  transition: transform 140ms ease;
}

.v2-faq details[open] summary span {
  transform: rotate(45deg);
}

.v2-faq details p {
  max-width: 720px;
  padding: 0 48px 25px 4px;
  color: #56524a;
  font-size: 15px;
  line-height: 1.75;
}

.v2-final {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 120px 24px;
  border-top: 2px solid var(--v2-ink);
  background: var(--v2-amber);
  text-align: center;
}

.v2-final > img {
  height: 78px;
}

.v2-final > span {
  margin-top: 24px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.15em;
}

.v2-final h2 {
  max-width: 940px;
  margin-top: 18px;
  font-size: clamp(54px, 7vw, 104px);
  font-weight: 900;
  letter-spacing: -0.07em;
  line-height: 0.98;
}

.v2-final > p {
  max-width: 640px;
  margin-top: 24px;
  font-size: 18px;
  line-height: 1.65;
}

.v2-final-actions {
  justify-content: center;
  margin-top: 36px;
}

.v2-final-link {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 14px 18px;
  color: var(--v2-ink);
  font-size: 15px;
  font-weight: 900;
  text-decoration: none;
}

.v2-final-link:hover {
  text-decoration: underline;
  text-underline-offset: 5px;
}

.v2-footer {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 20px;
  padding: 24px clamp(20px, 4vw, 64px);
  border-top: 2px solid var(--v2-ink);
}

.v2-brand-footer img {
  width: 30px;
  height: 30px;
}

.v2-footer p,
.v2-footer a {
  color: #555149;
  font-size: 12px;
  font-weight: 700;
}

.v2-footer a {
  justify-self: end;
  color: var(--v2-ink);
}

@media (max-width: 1060px) {
  .v2-hero {
    grid-template-columns: 1fr;
    width: min(100% - 40px, 820px);
    padding-block: 72px 92px;
  }

  .v2-hero::before {
    display: none;
  }

  .v2-proof-card {
    max-width: 620px;
    justify-self: center;
  }

  .v2-section-heading {
    grid-template-columns: 100px 1fr;
  }

  .v2-section-heading p {
    grid-column: 2;
  }

  .v2-privacy {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .v2-topbar {
    min-height: 64px;
  }

  .v2-nav > a:not(.v2-nav-board) {
    display: none;
  }

  .v2-nav .v2-nav-board {
    min-height: 36px;
    padding: 0 14px;
    font-size: 12px;
  }

  .v2-brand span {
    display: none;
  }

  .v2-hero {
    min-height: 0;
    padding: 56px 0 72px;
  }

  .v2-hero h1 {
    font-size: clamp(50px, 15vw, 72px);
  }

  .v2-hero h1 span {
    -webkit-text-stroke-width: 1.5px;
  }

  .v2-hero-copy > p br {
    display: none;
  }

  .v2-hero-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .v2-button {
    width: 100%;
  }

  .v2-proof-card {
    box-shadow: 8px 8px 0 var(--v2-amber);
    transform: none;
  }

  .v2-proof-total strong {
    font-size: 44px;
  }

  .v2-ticker {
    justify-content: flex-start;
    overflow: hidden;
  }

  .v2-ticker span:nth-of-type(n + 3),
  .v2-ticker i:nth-of-type(n + 2) {
    display: none;
  }

  .v2-section {
    padding: 78px 20px;
  }

  .v2-section-heading,
  .v2-section-heading-light,
  .v2-faq .v2-section-heading {
    grid-template-columns: 1fr;
    gap: 22px;
  }

  .v2-section-heading p {
    grid-column: auto;
  }

  .v2-value-grid {
    grid-template-columns: 1fr;
    margin-top: 46px;
  }

  .v2-value-card {
    min-height: 300px;
  }

  .v2-value-card + .v2-value-card {
    border-top: 2px solid var(--v2-ink);
    border-left: 0;
  }

  .v2-value-card h3 {
    margin-top: 64px;
  }

  .v2-statement {
    padding: 84px 20px;
  }

  .v2-statement p,
  .v2-statement strong {
    font-size: clamp(48px, 14vw, 70px);
  }

  .v2-step {
    grid-template-columns: 46px 1fr;
    gap: 16px;
  }

  .v2-step > svg {
    display: none;
  }

  .v2-how .v2-button-amber {
    width: 100%;
  }

  .v2-privacy-grid {
    grid-template-columns: 1fr;
    box-shadow: 7px 7px 0 var(--v2-amber);
  }

  .v2-data-card {
    min-height: 0;
  }

  .v2-data-card + .v2-data-card {
    border-top: 2px solid var(--v2-ink);
    border-left: 0;
  }

  .v2-faq-list {
    margin-top: 46px;
  }

  .v2-final {
    padding: 84px 20px;
  }

  .v2-final-actions {
    width: 100%;
    max-width: 360px;
  }

  .v2-footer {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
  }

  .v2-footer a {
    justify-self: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .v2-button,
  .v2-faq summary span {
    transition: none;
  }
}
</style>
